import {api} from '@/api'
import {RequestBodyType} from '@/api/singleMode/registerUser'
import {EScene} from '@/types/scene.types'
import {useGSAP} from '@gsap/react'
import AudioManager from '@repo/shared/AudioManager'
import {CompletedActions} from '@repo/shared/Customer/Coop/CompletedActions/CompletedActions'
import {ThanksModal} from '@repo/shared/Customer/Coop/ThanksModal/ThanksModal'
import {UserRegistrationModalForm} from '@repo/shared/Customer/Coop/UserRegistrationModalForm/UserRegistrationModalForm'
import {toastMessage} from '@repo/shared/Toast'
import {useTracking} from '@repo/shared/Tracking/Tracking.hooks'
import {getTrackingGameMode} from '@repo/shared/Tracking/Tracking.utils'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import clsx from 'clsx'
import gsap from 'gsap'
import {useAtomValue} from 'jotai'
import {FC, useEffect, useRef, useState} from 'react'
import ConfettiExplosion from 'react-confetti-explosion'
import {TransitionStatus} from 'react-transition-state'
import {getTimeAsNumberBetween} from '../Hud/Stopwatch/Stopwatch.utils'
import {useReset} from '../Reset/Reset.hooks'
import {
  gameModeState,
  singlePlayerDataState,
  storeWinnersDataState,
  timestampGameEndedState,
  timestampGameStartedState,
} from '../Store/atoms'

const audioManager = new AudioManager()

interface IScoreboardSinglePlayerProps {
  transitionStatus: TransitionStatus
}

const ScoreboardSingleplayer: FC<IScoreboardSinglePlayerProps> = ({transitionStatus}) => {
  const singlePlayerData = useAtomValue(singlePlayerDataState)
  const gameStartedTimestamp = useAtomValue(timestampGameStartedState)
  const gameEndedTimestamp = useAtomValue(timestampGameEndedState)
  const gameMode = useAtomValue(gameModeState)
  const storeWinnersData = useAtomValue(storeWinnersDataState)

  const {resetAll} = useReset()
  const {trackEvent} = useTracking()

  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const [isThanksModalOpen, setIsThanksModalOpen] = useState(false)
  const [isExploding, setIsExploding] = useState(false)

  const onRegisterUser = async (data: RequestBodyType) => {
    return api.singleMode
      .registerUser(data)
      .then(() => {
        setIsRegisterModalOpen(false)
        setIsThanksModalOpen(true)
      })
      .catch(() => {
        toastMessage('Kunne ikke registrere bruker')
      })
  }

  const enterTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const exitTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))

  const containerRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)

  const timeSpent = getTimeAsNumberBetween(
    (gameStartedTimestamp as number) ?? 0,
    gameEndedTimestamp ?? 0,
  )

  const isWin = (singlePlayerData?.time ?? 0) > timeSpent

  useGSAP(
    () => {
      /**
       * Enter animation
       */

      enterTimeline.current.set(containerRef.current, {opacity: 1})
      enterTimeline.current.set('.animate', {scale: 0, rotate: 3})
      enterTimeline.current.set(actionsRef.current, {scale: 0, rotate: 3})

      enterTimeline.current.to('.animate', {
        scale: 1,
        rotate: 0,
        duration: 1,
        delay: 0,
        stagger: 0.1,
        ease: 'elastic.out(1)',
        onStart: () => {
          if (isWin) {
            setIsExploding(true)
            audioManager.SFX.confetti?.seek(0)
            audioManager.SFX.clapping?.seek(0)
            audioManager.SFX.confetti?.play()
            audioManager.SFX.clapping?.play()
          }
        },
      })

      enterTimeline.current.to(actionsRef.current, {
        scale: 1,
        rotate: 0,
        duration: 1,
        delay: 1,
        ease: 'elastic.out(1)',
      })

      /**
       * Exit timeline
       */

      exitTimeline.current.to(containerRef.current, {
        opacity: 0,
        duration: 0.1,
      })
    },
    {
      dependencies: [isWin],
    },
  )

  /**
   * Enter and leave states
   */
  useEffect(() => {
    if (transitionStatus === 'entered') {
      debugLogger({
        message: 'Enter state',
        tag: 'Scoreboard [single]',
      })
      enterTimeline.current.play()
    } else if (transitionStatus === 'exiting') {
      debugLogger({
        message: 'Exit state',
        tag: 'Scoreboard [single]',
      })
      enterTimeline.current.pause()
      exitTimeline.current.play()
    }
  }, [transitionStatus])

  return (
    <div
      ref={containerRef}
      className={clsx(
        'fixed inset-0 flex select-none overflow-hidden bg-[rgba(62,89,224,0.8)]',
        'flex items-center',
      )}
    >
      <div
        id="scoreboard"
        className="flex max-h-[800px] flex-1 flex-col items-center justify-between px-[24px] pb-[48px] pt-[36px]"
      >
        <div className="mb-16 flex w-full justify-center px-[52px]">
          <img className="animate w-[200px] lg:w-[416px]" src="assets/logo.svg" />
        </div>

        <div className="animate flex max-w-[354px] flex-col items-center justify-center p-4">
          <div id="inspire-text" className="flex justify-center text-4xl uppercase text-white">
            {isWin ? 'Du klarte det!' : 'Det var nærme!'}
          </div>
          <div className="mb-6 mt-4 w-fit rounded-2xl bg-[#071c447a] px-10 py-4">
            <div
              id="spent-time"
              className={clsx(
                'text-center text-4xl font-semibold',
                isWin ? 'text-[#56cc89]' : 'text-[#ff7070]',
              )}
            >
              {timeSpent.toFixed(2)}
            </div>
            <div id="spent-time-text" className="text-center text-xl text-white">
              Tid brukt
            </div>
          </div>
        </div>

        <div className="animate max-w-[354px]">
          <CompletedActions
            text="Man kan delta uansett om man klarte tiden eller ikke"
            storeWinnersData={storeWinnersData}
            registrationClickHandler={() => {
              trackEvent({
                domain: EScene.SCOREBOARD,
                trigger: 'click',
                source: 'register_button',
                meta: {
                  gameMode: getTrackingGameMode(gameMode),
                },
              })

              setIsRegisterModalOpen(true)
            }}
            playAgainClickHandler={() => {
              trackEvent({
                domain: EScene.SCOREBOARD,
                trigger: 'click',
                source: 'play_again_button',
                meta: {
                  gameMode: getTrackingGameMode(gameMode),
                },
              })

              resetAll()
            }}
          />
        </div>
      </div>
      {isExploding && (
        <ConfettiExplosion
          className="fixed left-[50vw] top-[50vh]"
          colors={['#a67c00', '#bf9b30', '#ffbf00', '#ffcf40', '#ffdc73']}
          particleCount={120}
          particleSize={20}
          duration={3500}
          zIndex={99999}
          force={2}
          height={'100vh'}
          width={1800}
        />
      )}
      <UserRegistrationModalForm
        onSubmit={onRegisterUser}
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        isSprinten
      />
      <ThanksModal isOpen={isThanksModalOpen} onClose={() => setIsThanksModalOpen(false)} />
    </div>
  )
}

export default ScoreboardSingleplayer
